import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useEffect, useState } from "react";

import { useApiErrorMessage } from "@/shared/api";
import { Button } from "@/shared/ui/button";

import { useShareClip } from "../../api/useShareClip";
import type { HighlightView } from "../../model/Highlight";

const TITLE = msg`Link to the clip`;
const CREATE = msg`Create a link`;
const CREATE_HINT = msg`Anyone with the link will see this clip. Your other matches and analyses stay private.`;
const COPY = msg`Copy`;
const COPIED = msg`Copied`;
const OPENS = msg`Opens without signing in, the video plays right in Discord and Telegram`;
const COPIED_FOR_MS = 2000;

// Ссылка на клип `relock.gg/c/<код>`: создаётся только по кнопке — публичная страница
// есть лишь у клипов, которыми игрок решил поделиться.
export const ShareLink = ({ highlight }: { highlight: HighlightView }) => {
  const { i18n } = useLingui();
  const shareClip = useShareClip();
  const error = useApiErrorMessage(shareClip.error);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_FOR_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);
  const url = highlight.clip.share_url;
  const copy = () => {
    if (url === null) {
      return;
    }
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
    });
  };
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-border bg-background p-3">
      <span className="font-semibold">{i18n._(TITLE)}</span>
      {url === null ? (
        <>
          <Button
            variant="outline"
            className="self-start"
            loading={shareClip.isPending}
            onClick={() => {
              shareClip.share(highlight.id);
            }}
          >
            {i18n._(CREATE)}
          </Button>
          <span className="text-xs text-subtle">{i18n._(CREATE_HINT)}</span>
        </>
      ) : (
        <>
          <span className="flex gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-surface px-2.5 py-2.5 font-mono text-[13px] text-foreground-soft">
              {url.replace(/^https?:\/\//, "")}
            </code>
            <Button variant="outline" className="h-auto min-h-11" onClick={copy}>
              {i18n._(copied ? COPIED : COPY)}
            </Button>
          </span>
          <span className="text-xs text-subtle">{i18n._(OPENS)}</span>
        </>
      )}
      {error !== null && (
        <span role="alert" className="text-sm text-bad">
          {error}
        </span>
      )}
    </div>
  );
};
